/**
 * AdminContext.tsx
 * 
 * Admin-specific context for managing admin authentication and state.
 * Provides centralized admin functionality with proper type safety
 * and separation from regular user authentication.
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { AdminUser, AdminPermission } from '../types/admin'
import { adminAuthApi } from '../services/adminApi'

// Admin state interface
interface AdminState {
  user: AdminUser | null
  token: string | null
  isLoading: boolean
  error: string | null
  isAuthenticated: boolean
}

// Admin action types
type AdminAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ADMIN'; payload: { user: AdminUser; token: string } }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }

// Initial state
const initialState: AdminState = {
  user: null,
  token: localStorage.getItem('admin_token'),
  isLoading: false,
  error: null,
  isAuthenticated: false,
}

// Admin reducer
const adminReducer = (state: AdminState, action: AdminAction): AdminState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_ADMIN':
      localStorage.setItem('admin_token', action.payload.token)
      return { 
        ...state, 
        user: action.payload.user, 
        token: action.payload.token, 
        isLoading: false, 
        error: null,
        isAuthenticated: true
      }
    case 'SET_ERROR':
      return { ...state, isLoading: false, error: action.payload }
    case 'LOGOUT':
      localStorage.removeItem('admin_token')
      return { 
        ...state, 
        user: null, 
        token: null, 
        error: null, 
        isAuthenticated: false 
      }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    default:
      return state
  }
}

// Admin context interface
interface AdminContextType {
  state: AdminState
  dispatch: React.Dispatch<AdminAction>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  hasPermission: (permission: AdminPermission) => boolean
  clearError: () => void
}

const AdminContext = createContext<AdminContextType | undefined>(undefined)

// Admin provider component
export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(adminReducer, initialState)

  // Check for existing admin token on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      // Validate admin token with backend
      const validateAdminToken = async () => {
        try {
          const response = await fetch('http://localhost:8000/admin/validate', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
          
          if (response.ok) {
            const data = await response.json()
            if (data.success && data.user) {
              // Restore admin session with real data
              const adminUser: AdminUser = {
                id: data.user.id,
                email: data.user.email,
                name: data.user.name,
                role: data.user.role,
                permissions: data.user.permissions || [],
                lastLogin: data.user.last_login || new Date().toISOString(),
                createdAt: data.user.created_at || new Date().toISOString(),
                updatedAt: data.user.updated_at || new Date().toISOString()
              }
              
              dispatch({
                type: 'SET_ADMIN',
                payload: { user: adminUser, token: token },
              })
            } else {
              // Invalid token, clear it
              localStorage.removeItem('admin_token')
            }
          } else {
            // Invalid token, clear it
            localStorage.removeItem('admin_token')
          }
        } catch (error) {
          console.error('Error validating admin token:', error)
          // Clear token on error
          localStorage.removeItem('admin_token')
        }
      }
      
      validateAdminToken()
    }
  }, [])

  // Admin login function
  const login = async (email: string, password: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'CLEAR_ERROR' })
    
    try {
      const response = await fetch('http://localhost:8000/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          password: password
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        // Store admin token
        localStorage.setItem('admin_token', data.token)
        
        // Create admin user object
        const adminUser: AdminUser = {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name,
          role: data.user.role,
          permissions: data.user.permissions || [],
          lastLogin: new Date().toISOString(),
          createdAt: data.user.created_at || new Date().toISOString(),
          updatedAt: data.user.updated_at || new Date().toISOString()
        }
        
        dispatch({
          type: 'SET_ADMIN',
          payload: {
            user: adminUser,
            token: data.token
          }
        })
      } else {
        dispatch({
          type: 'SET_ERROR',
          payload: data.detail || 'Login failed'
        })
      }
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Login failed'
      })
    }
  }

  // Admin logout function
  const logout = async () => {
    try {
      const token = localStorage.getItem('admin_token')
      if (token) {
        // Call backend logout endpoint
        await fetch('http://localhost:8000/admin/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      }
    } catch (error) {
      console.error('Error during admin logout:', error)
    } finally {
      // Clear local storage and state regardless of API call result
      localStorage.removeItem('admin_token')
      dispatch({ type: 'LOGOUT' })
    }
  }

  // Permission check function
  const hasPermission = (permission: AdminPermission): boolean => {
    if (!state.user) return false
    return state.user.permissions.includes(permission)
  }

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const value: AdminContextType = {
    state,
    dispatch,
    login,
    logout,
    hasPermission,
    clearError
  }

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  )
}

// Admin hook
export const useAdmin = () => {
  const context = useContext(AdminContext)
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider')
  }
  return context
} 